import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, X, Settings as SettingsIcon } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
} from './ui/dropdown-menu';
import { notificationAPI } from '../lib/api';
import api from '../lib/api';
import { useWorkspace } from '../contexts/WorkspaceContext';

// 2026-02-20 — Per-event email toggles shown inside the bell's
// preferences panel. Stored on `db.notification_settings` keyed by
// (workspace_id, user_id) via the legacy `/api/notification-settings/{ws}`
// endpoint, which `send_user_email` already honors (line 1066-1072 of
// server.py). Keep this list short and high-value — the dedupe layer
// already removes spam, so users only need to opt out of categories
// they genuinely don't want.
const EMAIL_PREF_ROWS = [
  { key: 'task_assigned',        label: 'When a task is assigned to me',     hint: 'Email + bell. Always on by default.' },
  { key: 'task_status_changed',  label: 'When a task moves to Review',       hint: 'Email + bell. Sent to the task creator.' },
  { key: 'mention',              label: 'When someone @mentions me',         hint: 'Email + bell. Sent for every mention.' },
];

/**
 * Global notification bell — Iter 61 Part 4.
 *
 * Rendered in the persistent top-right shell header so it's accessible on
 * every page (Tasks, Clients, Calendar, Settings, etc.) — not just the
 * Dashboard. Click-to-deep-link behavior (navigates to `n.link` on click)
 * continues to work from the earlier notifications iteration.
 *
 * Polls every 30s. Unread dot renders when any `n.read === false`.
 */
export default function NotificationBell({ className = '' }) {
  const [items, setItems] = useState([]);
  const navigate = useNavigate();
  // 2026-02-20 — Per-event email preferences panel state. Kept INSIDE
  // the bell so the user adjusts settings exactly where they consume
  // them. Loaded lazily on first open and cached for the session.
  const { currentWorkspace } = useWorkspace();
  const wsId = currentWorkspace?.id;
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [prefs, setPrefs] = useState(null); // { event_type: {email, inapp} }
  const [prefsSaving, setPrefsSaving] = useState(false);

  const loadPrefs = () => {
    if (!wsId) return;
    api.get(`/notification-settings/${wsId}`)
      .then((r) => setPrefs(r.data || {}))
      .catch(() => setPrefs({}));
  };

  const togglePref = (key) => {
    if (!wsId || !prefs) return;
    const current = prefs[key] || { email: true, inapp: true };
    const next = { ...current, email: !current.email };
    const optimistic = { ...prefs, [key]: next };
    setPrefs(optimistic);
    setPrefsSaving(true);
    api.put(`/notification-settings/${wsId}`, optimistic)
      .catch(() => setPrefs(prefs)) // rollback on failure
      .finally(() => setPrefsSaving(false));
  };

  const fetchItems = () => {
    notificationAPI.list(20)
      .then((res) => setItems(res.data?.items || []))
      .catch(() => {});
  };

  // 2026-02-20 — When the bell dropdown opens, mark every visible
  // notification as read on the server in one shot so the badge count
  // drops to 0. Spec: "Once the user opens the notification panel and
  // sees a notification, it should be marked as read and the count
  // should decrease." Local state is mirrored optimistically; the
  // dropdown still lets the user click into individual rows to navigate.
  const markAllVisibleRead = () => {
    setItems((prev) => prev.map((x) => (x.read ? x : { ...x, read: true })));
    notificationAPI.markAllRead?.().catch(() => {});
  };

  useEffect(() => {
    fetchItems();
    const t = setInterval(fetchItems, 30000);
    return () => clearInterval(t);
  }, []);

  const unread = items.filter((n) => !n.read).length;

  const openOne = (n) => {
    if (!n.read) {
      notificationAPI.markRead(n.id).catch(() => {});
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    }
    if (n.link) navigate(n.link);
  };

  const discard = (e, id) => {
    e.stopPropagation();
    notificationAPI.discard(id).catch(() => {});
    setItems((prev) => prev.filter((x) => x.id !== id));
  };

  const clearAll = () => {
    notificationAPI.clearAll().catch(() => {});
    setItems([]);
  };

  return (
    <DropdownMenu onOpenChange={(open) => { if (open) markAllVisibleRead(); }}>
      <DropdownMenuTrigger asChild>
        <button
          className={`relative p-1.5 rounded-md text-foreground/70 hover:text-gold hover:bg-foreground/[0.06] transition-colors ${className}`}
          data-testid="global-notification-bell"
          aria-label={unread > 0 ? `${unread} unread system notifications` : 'System notifications'}
          title={unread > 0 ? `${unread} unread system notifications (separate from chat)` : 'System notifications'}
        >
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span
              className="absolute top-0.5 right-0.5 h-2 w-2 rounded-full bg-red-500 ring-2 ring-background"
              data-testid="global-notification-bell-dot"
            />
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-[360px] max-h-[460px] overflow-y-auto p-2"
        data-testid="global-notification-dropdown"
      >
        <div className="flex items-center justify-between px-2 py-1 mb-1">
          <p className="text-[10px] font-semibold text-muted-foreground/70 uppercase tracking-wider">
            Notifications {unread > 0 && <span className="text-gold ml-1">({unread})</span>}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (!prefsOpen && !prefs) loadPrefs();
                setPrefsOpen((o) => !o);
              }}
              className={`p-0.5 rounded transition-colors ${prefsOpen ? 'text-gold bg-foreground/[0.06]' : 'text-muted-foreground/40 hover:text-gold'}`}
              data-testid="global-notification-prefs-toggle"
              aria-label="Notification preferences"
              title="Notification preferences"
            >
              <SettingsIcon className="h-3 w-3" />
            </button>
            {items.length > 0 && (
              <button
                onClick={clearAll}
                className="text-[9px] text-red-500/60 hover:text-red-400"
                data-testid="global-notification-clear-all"
              >
                Clear all
              </button>
            )}
          </div>
        </div>
        {/* 2026-02-20 — Preferences panel. Collapsible. Per-event email
            toggle. In-app toggle deliberately not exposed for these 3
            high-value events — turning OFF in-app for "task assigned"
            would mean the user never knows a task was assigned, which
            defeats the app. Email is the noise-reduction lever. */}
        {prefsOpen && (
          <div
            className="rounded-md border border-border/30 bg-foreground/[0.02] p-2 mb-2 space-y-2"
            data-testid="global-notification-prefs-panel"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground/70 font-semibold">Email preferences</span>
              {prefsSaving && <span className="text-[9px] text-muted-foreground/50">saving…</span>}
            </div>
            {!wsId ? (
              <p className="text-[10px] text-muted-foreground/60">Pick a workspace first.</p>
            ) : prefs === null ? (
              <p className="text-[10px] text-muted-foreground/60">Loading…</p>
            ) : (
              EMAIL_PREF_ROWS.map(({ key, label, hint }) => {
                const row = prefs[key] || { email: true, inapp: true };
                const emailOn = row.email !== false; // default ON
                return (
                  <label
                    key={key}
                    className="flex items-start gap-2 cursor-pointer py-1 select-none"
                    data-testid={`global-notification-prefs-row-${key}`}
                  >
                    <input
                      type="checkbox"
                      checked={emailOn}
                      onChange={() => togglePref(key)}
                      className="mt-0.5 h-3 w-3 accent-gold cursor-pointer"
                      data-testid={`global-notification-prefs-${key}-email`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] text-foreground">{label}</div>
                      <div className="text-[9.5px] text-muted-foreground/60">{hint}</div>
                    </div>
                  </label>
                );
              })
            )}
            <p className="text-[9px] text-muted-foreground/40 leading-snug pt-1 border-t border-border/20">
              In-app notifications continue regardless — these toggles only affect emails. The bell panel always shows everything, deduped automatically.
            </p>
          </div>
        )}
        {items.length === 0 ? (
          <p className="text-[12px] text-muted-foreground/50 text-center py-8">
            You're all caught up.
          </p>
        ) : (
          // 2026-07-15 — grouped by DATE, newest day first, so "what
          // happened today" is instantly scannable.
          (() => {
            const dateHeader = (iso) => {
              const d = iso ? new Date(iso) : null;
              if (!d || Number.isNaN(d.getTime())) return 'Earlier';
              return d.toLocaleDateString(undefined, {
                weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
              });
            };
            const shown = [...items]
              .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
              .slice(0, 15);
            const groups = [];
            for (const n of shown) {
              const h = dateHeader(n.created_at);
              if (!groups.length || groups[groups.length - 1].header !== h) {
                groups.push({ header: h, rows: [] });
              }
              groups[groups.length - 1].rows.push(n);
            }
            return groups.map((g) => (
              <div key={g.header}>
                <div
                  className="text-[11px] font-semibold text-foreground/75 px-1 pt-2.5 pb-1 mb-1 border-b border-border/30"
                  data-testid="notif-date-header"
                >
                  {g.header}
                </div>
                {g.rows.map((n) => (
                  <div
                    key={n.id}
                    className={`flex items-start gap-2 p-2 rounded-md mb-0.5 hover:bg-foreground/[0.03] cursor-pointer ${n.read ? 'opacity-55' : ''}`}
                    onClick={() => openOne(n)}
                    data-testid={`global-notification-item-${n.id}`}
                  >
                    {!n.read
                      ? <span className="h-1.5 w-1.5 rounded-full bg-gold mt-1.5 shrink-0" />
                      : <span className="w-1.5 shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <p className={`text-[11px] ${n.read ? '' : 'font-medium'}`}>{n.title}</p>
                      {n.message && (
                        <p className="text-[10px] text-muted-foreground line-clamp-2">{n.message}</p>
                      )}
                    </div>
                    <button
                      onClick={(e) => discard(e, n.id)}
                      className="p-0.5 shrink-0 opacity-40 hover:opacity-100"
                      aria-label="Dismiss"
                    >
                      <X className="h-2.5 w-2.5 text-muted-foreground hover:text-red-500" />
                    </button>
                  </div>
                ))}
              </div>
            ));
          })()
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
